/**
 * CheckpointCoverage — the report's honesty panel (viewpoint observability
 * Phase 1).
 *
 * Two pieces, both driven by `evaluation.metadata.coverage`:
 *
 *   <CoverageLine>          — one sentence on the hero saying how many of the
 *                             coach's checkpoints this video could be scored
 *                             against, why the rest weren't, and what to change.
 *                             Renders NOTHING when every checkpoint was
 *                             assessed (the common path) or on any evaluation
 *                             produced before the contract (no `coverage` key).
 *
 *   <WithheldCheckpoints>   — one row per withheld checkpoint, naming it with
 *                             the coach's own label and giving the reason in
 *                             plain language.
 *
 * Visible to ALL roles: an athlete has at least as much right to know a check
 * was not scored as the coach does, and the copy contains no coach-only
 * numbers. A withheld checkpoint is never shown as a score of 0 — that is the
 * whole point of the panel (LFT-063 sentinel rule, carried to the UI).
 *
 * All copy comes from `@/utils/checkpointCoverageCopy`; this file is layout
 * only. See docs/COACH_FACING_COPY_STANDARD.md.
 */
import { EyeSlashIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { cn } from '@/utils/cn';
import type { EvaluationCoverage } from '@/types/api';
import {
  buildCoverageSentences,
  withheldCheckpointHeadline,
  withheldCheckpointReason,
} from '@/utils/checkpointCoverageCopy';

// ---------------------------------------------------------------------------
// Coverage line
// ---------------------------------------------------------------------------

export interface CoverageLineProps {
  coverage?: EvaluationCoverage | null;
  className?: string;
}

export function CoverageLine({ coverage, className }: CoverageLineProps) {
  const sentences = buildCoverageSentences(coverage);
  if (!sentences) return null;

  return (
    <div
      role="note"
      aria-label="Checkpoint coverage"
      data-testid="eval-coverage-line"
      className={cn(
        'flex items-start gap-2.5 rounded-lg border border-border-subtle',
        'bg-dark-elevated-1/70 px-4 py-3',
        className,
      )}
    >
      <InformationCircleIcon
        className="mt-0.5 h-4 w-4 flex-shrink-0 text-text-muted"
        aria-hidden="true"
      />
      <p className="text-sm leading-relaxed text-text-secondary">
        <span className="font-semibold text-text-primary">{sentences.what}</span>{' '}
        {sentences.why} {sentences.how}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Withheld checkpoint rows
// ---------------------------------------------------------------------------

export interface WithheldCheckpointsProps {
  coverage?: EvaluationCoverage | null;
  className?: string;
}

export function WithheldCheckpoints({ coverage, className }: WithheldCheckpointsProps) {
  const abstained = coverage?.abstained ?? [];
  if (abstained.length === 0) return null;

  return (
    <section
      aria-label="Checkpoints we could not score"
      data-testid="eval-withheld-checkpoints"
      className={cn('space-y-2', className)}
    >
      <h3 className="font-heading text-xs font-semibold uppercase tracking-widest text-text-muted">
        Not scored from this video
      </h3>
      <ul className="space-y-2">
        {abstained.map((item, index) => (
          <li
            key={item.template_annotation_id || `${item.label}-${index}`}
            data-testid="eval-withheld-checkpoint-row"
            className="flex items-start gap-2.5 rounded-lg border border-border-subtle bg-dark-elevated-1/70 px-4 py-3"
          >
            <EyeSlashIcon
              className="mt-0.5 h-4 w-4 flex-shrink-0 text-text-muted"
              aria-hidden="true"
            />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-text-primary">
                {withheldCheckpointHeadline(item.label)}
              </p>
              <p className="mt-0.5 text-sm leading-relaxed text-text-secondary">
                {withheldCheckpointReason(item.reason, item.body_part)}
              </p>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

export default CoverageLine;
